import type { RefCallbackWithCleanup } from 'react';

/**
 * A factory function which creates a ref callback that will invoke the provided
 * callback when the attached element is mounted or resized.
 * @param callback - The callback to call when the attached element is mounted or resized.
 * @param cleanupCallback - An optional callback that will be called when the ref is unmounted.
 * @returns A ref callback that can be attached to a React element.
 * @example
 * const adjustPaddingRef = useMemo(
 *  () =>
 *    createOnResizeRef<HTMLDivElement>(
 *      (elt) => setPaddingBottom(elt.getBoundingClientRect().height),
 *      () => setPaddingBottom(0)
 *    ),
 *  []
 * );
 *
 * return <>
 *  {condition && <div ref={adjustPaddingRef}>...</div>}
 *  <div style={{ paddingBottom }}>...</div>
 * </>;
 */
export function createOnResizeRef<T extends Element>(
  callback: (node: T) => void,
  cleanupCallback?: (node: T) => void
): RefCallbackWithCleanup<T> {
  return (node: T) => {
    const resizeObserver = new ResizeObserver(() => {
      callback(node);
    });

    resizeObserver.observe(node);

    return () => {
      cleanupCallback?.(node);
      resizeObserver.disconnect();
    };
  };
}
